import React, { useMemo } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { getStateConfig } from '../../../../../utils/taskStateUtils';
import { formatDate } from '../utils/recurrenceFormatters';
import { AttachmentList } from './AttachmentList';
import StaffMember from '../../../../../../../components/common/StaffMember';

export function RecurrenceHistoryTable({
  chainTasks,
  currentTaskId,
  onViewTask,
  totalCount,
  paginationModel,
  onPaginationModelChange,
  loading,
}) {
  const { t } = useTranslation(['common', 'tasks']);

  const columns = useMemo(() => [
    {
      field: 'due_date',
      headerName: t('tasks:labels.due_date'),
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2">{formatDate(row.due_date)}</Typography>
      ),
    },
    {
      field: 'completion_date',
      headerName: t('tasks:labels.completion_date'),
      width: 230,
      sortable: false,
      renderCell: ({ row }) => {
        const stateConfig = getStateConfig(row.state);
        return row.completion_date ? (
          <Box display="flex" alignItems="center" gap={1} height="100%">
            <Typography variant="body2">{formatDate(row.completion_date)}</Typography>
            <Chip label={stateConfig.label} size="small" color={stateConfig.color} />
          </Box>
        ) : (
          <Box display="flex" alignItems="center" height="100%">
            <Chip label={stateConfig.label} size="small" color={stateConfig.color} variant="outlined" />
          </Box>
        );
      },
    },
    {
      field: 'completed_by_user',
      headerName: t('tasks:labels.completed_by'),
      width: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.completed_by_user ? (
            <StaffMember
              staffMember={row.completed_by_user}
              avatarSize={32}
              imageSize="32x32"
              nameVariant="body2"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">-</Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'completion_notes',
      headerName: t('tasks:labels.notes'),
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.completion_notes || '-'}
        </Typography>
      ),
    },
    {
      field: 'attachments',
      headerName: t('tasks:labels.attachments'),
      width: 150,
      sortable: false,
      renderCell: ({ row }) => <AttachmentList attachments={row.attachments} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" height="100%">
          {row.task_id === currentTaskId ? (
            <Chip
              label={t('tasks:recurrence_history.current')}
              size="small"
              color="info"
            />
          ) : (
            <Tooltip title={t('tasks:recurrence_history.view_task')}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onViewTask(row.task_id); }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [t, currentTaskId, onViewTask]);

  return (
    <DataGrid
      rows={chainTasks ?? []}
      columns={columns}
      getRowId={(row) => row.task_id}
      rowCount={totalCount ?? 0}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={[25, 50, 100]}
      loading={loading}
      disableColumnFilter
      disableColumnMenu
      disableRowSelectionOnClick
      rowHeight={64}
      onRowClick={({ row }) => onViewTask(row.task_id)}
      getRowClassName={({ row }) => row.task_id === currentTaskId ? 'current-task-row' : ''}
      sx={{
        '& .MuiDataGrid-row': { cursor: 'pointer' },
        '& .current-task-row': { bgcolor: 'primary.50' },
        minHeight: 600,
        maxHeight: 600,
      }}
    />
  );
}
